import { onMounted, onBeforeUnmount } from '#imports';

/**
 * Attaches a single passive `scroll` listener that invokes `callback` at most
 * once per animation frame. A non-passive or unthrottled scroll handler blocks
 * the compositor thread and inflates INP on mobile — this keeps the work
 * coalesced and off the input-handling critical path.
 *
 * Auto-detaches on unmount.
 *
 * @param {() => void} callback
 * @param {{ immediate?: boolean }} [options] run `callback` once on mount
 */
export function useScrollListener(callback, { immediate = true } = {}) {
  let scheduled = false;
  let frame = 0;

  const run = () => {
    scheduled = false;
    callback();
  };

  const onScroll = () => {
    if (scheduled) return;
    scheduled = true;
    frame = requestAnimationFrame(run);
  };

  onMounted(() => {
    window.addEventListener('scroll', onScroll, { passive: true });
    if (immediate) callback();
  });

  onBeforeUnmount(() => {
    window.removeEventListener('scroll', onScroll);
    cancelAnimationFrame(frame);
  });
}
